import { useCallback, useEffect, useRef, useState } from "react";
import { usePost } from "./usePost";
import { useToast } from "../store/ToastProvider";
import { AIJobStartResponse } from "../types/compassAITypes";
import { AssessEngagementV2Request } from "../types/engagementAssessmentV2Types";
import { CompassError } from "../types/errorTypes";
import { MESSAGES } from "../messages";

interface UseAssessEngagementV2Return {
  jobId: string | null;
  isLoading: boolean;
  error: CompassError | null;
  triggerAssessment: (
    payload: AssessEngagementV2Request,
  ) => Promise<string | null>;
  reset: () => void;
}

export function useAssessEngagementV2(): UseAssessEngagementV2Return {
  const { addToast } = useToast();
  const { data, error, isLoading, sendData } = usePost<
    AIJobStartResponse,
    AssessEngagementV2Request
  >();

  const [jobId, setJobId] = useState<string | null>(null);
  const lastToastedErrorRef = useRef<CompassError | null>(null);

  useEffect(() => {
    if (error && error !== lastToastedErrorRef.current) {
      lastToastedErrorRef.current = error;
      addToast(
        MESSAGES.ENGAGEMENT_ASSESSMENT_V2.ERRORS.SINGLE_TRIGGER_FAILED,
        "error",
      );
    }
  }, [error, addToast]);

  const triggerAssessment = useCallback(
    async (payload: AssessEngagementV2Request): Promise<string | null> => {
      const response = await sendData("AI_ASSESS_ENGAGEMENT_V2", payload);

      if (!response?.job_id) {
        if (!error) {
          addToast(
            MESSAGES.ENGAGEMENT_ASSESSMENT_V2.ERRORS.SINGLE_TRIGGER_FAILED,
            "error",
          );
        }
        setJobId(null);
        return null;
      }

      setJobId(response.job_id);
      return response.job_id;
    },
    [sendData, addToast, error],
  );

  const reset = useCallback((): void => {
    setJobId(null);
    lastToastedErrorRef.current = null;
  }, []);

  return {
    jobId: jobId ?? data?.job_id ?? null,
    isLoading,
    error,
    triggerAssessment,
    reset,
  };
}
